import { useId, type ReactNode } from 'react';

import { FormFieldError } from '@/components/ui/form-field-error';
import { Label } from '@/components/ui/label';
import { cn } from '@/utils/cn';

export interface FormFieldProps {
  label: string;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    invalid: boolean;
  }) => ReactNode;
  hint?: string;
  error?: string;
  required?: boolean;
  requiredLabel?: string;
  optionalLabel?: string;
  className?: string;
}

export const FormField = ({
  label,
  children,
  hint,
  error,
  required = false,
  requiredLabel,
  optionalLabel,
  className,
}: FormFieldProps) => {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id} required={required} requiredLabel={requiredLabel}>
          {label}
        </Label>
        {!required && optionalLabel ? (
          <span className="text-caption text-fg-tertiary">{optionalLabel}</span>
        ) : null}
      </div>

      {children({
        id,
        'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
        invalid: Boolean(error),
      })}

      {hint && !error ? (
        <p id={hintId} className="text-caption text-fg-secondary">
          {hint}
        </p>
      ) : null}

      <FormFieldError id={errorId} message={error} />
    </div>
  );
};
